/**
 * Pure reorder engine (WR1 SR1.1) — the v1 replenishment-suggestion formula as
 * a zero-I/O module, trace-first, shared VERBATIM by the MSW handler
 * (`mocks/handlers/reorder.ts`, SR2.1) and the review-queue UI (SR3.1). No I/O,
 * no globals, no `Date.now` — the caller supplies every input AND the read
 * moment, so the handler, the UI, and Joe's future backend can never drift
 * (the same handler-as-spec pattern as `resolvePrice`, portable verbatim).
 *
 * This is a DERIVED READ MODEL: a suggestion is regenerated per read and is
 * NEVER hand-edited or stored. Edits happen on the draft PO the planner creates
 * from a suggestion, never on the suggestion itself.
 *
 * The formula is NetSuite's Order-Items page with substituted, two-tier inputs
 * (research--netsuite-reorder; plan §SR1.1 + Appendix D-1):
 *   inventoryPosition = 可供量(Tier-1 read-through) + qtyOpenToReceive(Tier-2 derived)
 *   trigger:  inventoryPosition < reorderPoint            (below the position, saved-search parity)
 *   suggested = (preferredStockLevel + pooledDemandQty) − inventoryPosition
 *               clamped ≥ moq, rounded UP to reorderMultiple
 * Blank-param and doNotOrder / inactive / not-Ready products are EXCLUDED (they
 * never appear — NetSuite's own blank-params-never-listed parity). There is NO
 * computed/auto reorder point in v1 (Appendix D-3; the v2 ladder owns that) —
 * the planner's stored number is authoritative.
 *
 * The on-order term is the Tier-2 derived `qtyOpenToReceive` (an ERP-created PO
 * must suppress re-suggestion immediately, independent of sync cadence); the
 * Tier-1 `qtyOnOrder` echo is RECONCILIATION reference only — a variance beyond
 * tolerance downgrades confidence, never the input. Whether NetSuite's value
 * includes synced ERP POs is OQ-7; the engine already takes both, so a ruling
 * is a one-line call-site change, not a formula rewrite.
 */

import {
  freshnessTierOf,
  isReconciliationVariance,
  isStaleTier,
  reconciliationDelta,
  type FreshnessTier,
} from './staleness';

// ---------------------------------------------------------------------------
// Inputs — typed by PROVENANCE so a call site can never feed a Tier-1 number
// where a Tier-2 fact is required (the boundary is in the type system).
// ---------------------------------------------------------------------------

/** Product identity + gating flags (Tier-2, from the product master). */
export interface ReorderProductContext {
  productId: string;
  /** Optional shared cross-company link group (carried through to the row). */
  scn?: string | null;
  /** Primary vendor line's vendorId; `null` = no preferred vendor (US-266). */
  primaryVendorId: number | null;
  isActive: boolean;
  /** 產品就緒 — only Ready products may enter SO/PO, so only they may suggest. */
  isReady: boolean;
}

/**
 * Tier-1 NetSuite availability read-through echo. `availableQty` is 可供量;
 * `qtyOnOrder` / `preferredStockLevel` / `reorderPoint` are reconciliation
 * echoes only. `syncedAt` rides through onto the derived suggestion.
 */
export interface Tier1Snapshot {
  availableQty: number;
  qtyOnOrder: number | null;
  preferredStockLevel?: number | null;
  reorderPoint?: number | null;
  syncedAt: string;
}

/**
 * Tier-2 replenishment params (the planner's stored numbers — authoritative).
 * `reorderPoint` / `preferredStockLevel` blank (`null`) excludes the product.
 */
export interface Tier2Params {
  reorderPoint: number | null;
  preferredStockLevel: number | null;
  reorderMultiple?: number | null;
  moq?: number | null;
  doNotOrder: boolean;
  preOrder: boolean;
}

/** Tier-2 derived open-PO inbound balance (occ selectors, derived-never-stored). */
export interface Tier2Supply {
  qtyOpenToReceive: number;
}

/** Tier-2 pooled short demand for this product (the demand-pool selector). */
export interface Tier2Demand {
  pooledDemandQty: number;
}

export interface ReorderSuggestionInput {
  product: ReorderProductContext;
  snapshot: Tier1Snapshot;
  params: Tier2Params;
  supply: Tier2Supply;
  demand: Tier2Demand;
  /**
   * Trailing-180d average daily demand (SR1.3 shared selector) — DISPLAY/SORT
   * only (coverage-days column). NEVER a formula input in v1 (Appendix D-3/D-7).
   * `null`/absent = the coverage column has no data for this row.
   */
  avgDailyDemand?: number | null;
  /**
   * Trailing sales-history span in days (SR1.3) — a CONFIDENCE input: thin
   * history (< 90d) downgrades confidence when the coverage column is active.
   */
  historyDays?: number | null;
  /** The read moment (ISO). Caller-supplied — a pure fn never calls Date.now. */
  computedAt: string;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

export type ReorderSeverity = 'high' | 'medium' | 'low';
export type ReorderConfidence = 'ok' | 'low';

/**
 * Confidence cause set — kept SEPARATE from the scalar so the UI can attribute
 * FRESHNESS (`stale`) to the 可供量 SourceStampTag and roll ONLY the non-freshness
 * causes (`variance` + `thinHistory`) into the 低信心 badge (one cause never lights
 * two chips on a row — UX-lens adjustment 2 / Q7).
 */
export interface ReorderConfidenceCauses {
  /** Snapshot is stale/expired (owned by the SourceStampTag, not the badge). */
  stale?: boolean;
  /** Tier-1 qtyOnOrder disagrees with Tier-2 qtyOpenToReceive beyond tolerance. */
  variance?: boolean;
  /** Thin sales history (< 90d) while the coverage column is active. */
  thinHistory?: boolean;
}

/**
 * A semantic classification carrying tone AND icon AND label together — so a
 * severity is never three hues at the same weight (UX-lens adjustment 5 / Q5).
 * Kept as DATA (tone name + icon key + i18n key), not React, so the engine
 * stays zero-I/O and backend-portable; SR3.1 maps these to tokens + antd icons.
 */
export interface SemanticType {
  /** Tone name — the UI resolves to a token colour, never a raw hex. */
  tone: 'danger' | 'warning' | 'info';
  /** Icon key — the UI maps to an antd icon; kept as data to stay I/O-free. */
  iconKey: 'alert' | 'warning' | 'info';
  /** i18n key for the severity label (SR2.4 procurement namespace). */
  labelKey: string;
}

/**
 * Each severity tier maps to exactly one SemanticType (tone + icon + label).
 * high → danger, medium → warning, low → info. SR3.1 consumes this so the queue
 * never invents its own three same-weight hues.
 */
export const REORDER_SEVERITY_SEMANTICS: Record<ReorderSeverity, SemanticType> = {
  high: {
    tone: 'danger',
    iconKey: 'alert',
    labelKey: 'procurement:demandPool.reorder.severity.high',
  },
  medium: {
    tone: 'warning',
    iconKey: 'warning',
    labelKey: 'procurement:demandPool.reorder.severity.medium',
  },
  low: {
    tone: 'info',
    iconKey: 'info',
    labelKey: 'procurement:demandPool.reorder.severity.low',
  },
};

/** One derived reorder suggestion (regenerated per read, never stored). */
export interface ReorderSuggestion {
  productId: string;
  scn?: string | null;
  primaryVendorId: number | null;
  /** 可供量 + qtyOpenToReceive — the position the trigger tests, not raw available. */
  inventoryPosition: number;
  /** Tier-2 authoritative reorder point (再訂購點). */
  rop: number;
  /** Tier-2 preferred stock level / 目標水位. */
  psl: number;
  pooledDemandQty: number;
  /** Tier-2 derived on-order (the formula input). */
  qtyOpenToReceive: number;
  /** Tier-1 on-order echo (reconciliation reference; `null` = not synced). */
  netsuiteQtyOnOrder: number | null;
  /** qtyOnOrder − qtyOpenToReceive (`null` when the Tier-1 echo is not synced). */
  netsuiteDelta: number | null;
  /** Final order-quantity suggestion: MOQ-clamped, rounded up to the multiple. */
  suggestedQty: number;
  /** Pre-clamp/pre-round quantity, kept for the drawer waterfall trace. */
  rawQty: number;
  /** The MOQ floor raised the raw quantity. */
  moqClamped: boolean;
  /** Rounding up to the reorder multiple changed the quantity. */
  multipleRounded: boolean;
  /**
   * Days of coverage at the position (position ÷ avgDailyDemand), floored and
   * capped at 365. `null` when avgDailyDemand is absent/≤0 (never Infinity/NaN)
   * — the UI renders `—` / `>365d`. NEUTRAL/informational; no colour-by-value.
   */
  coverageDays: number | null;
  severity: ReorderSeverity;
  confidence: ReorderConfidence;
  confidenceCauses: ReorderConfidenceCauses;
  /** The snapshot's freshness tier (drives the SourceStampTag warning). */
  freshness: FreshnessTier;
  /** The read-through stamp, carried onto the derived artifact (staleness contract). */
  syncedAt: string;
  computedAt: string;
}

/** Coverage-days display cap — beyond this the UI renders `>365d`. */
export const COVERAGE_DAYS_CAP = 365;

/** Below-90-days is "thin" sales history for confidence purposes. */
export const THIN_HISTORY_DAYS = 90;

// ---------------------------------------------------------------------------
// Exclusions (NetSuite parity — blank-param items never appear)
// ---------------------------------------------------------------------------

export type ReorderExclusionReason =
  | 'inactive'
  | 'not_ready'
  | 'do_not_order'
  | 'blank_reorder_point'
  | 'blank_preferred_stock_level';

/**
 * Why a product is kept OUT of the suggestion queue entirely (not merely
 * not-triggered). Returns `null` when the product is eligible to be evaluated.
 * Order matters only for which reason is reported; any one excludes.
 */
export function suggestionExclusionReason(
  product: ReorderProductContext,
  params: Tier2Params
): ReorderExclusionReason | null {
  if (!product.isActive) return 'inactive';
  if (!product.isReady) return 'not_ready';
  if (params.doNotOrder) return 'do_not_order';
  if (params.reorderPoint == null) return 'blank_reorder_point';
  if (params.preferredStockLevel == null) return 'blank_preferred_stock_level';
  return null;
}

// ---------------------------------------------------------------------------
// Pure building blocks (exported for vitest)
// ---------------------------------------------------------------------------

export interface SuggestedQtyTrace {
  rawQty: number;
  suggestedQty: number;
  moqClamped: boolean;
  multipleRounded: boolean;
}

/**
 * suggestedQty = (PSL + pooledDemand) − inventoryPosition, clamped ≥ moq, then
 * rounded UP to the reorder multiple. `rawQty` (pre-clamp/round), `moqClamped`,
 * and `multipleRounded` are kept for the drawer trace. The MOQ floor defaults
 * to 0 (which also floors the degenerate negative-raw misconfig at 0); the
 * multiple is skipped when absent/≤0.
 */
export function computeSuggestedQty(
  preferredStockLevel: number,
  pooledDemandQty: number,
  inventoryPosition: number,
  moq?: number | null,
  reorderMultiple?: number | null
): SuggestedQtyTrace {
  const rawQty = preferredStockLevel + pooledDemandQty - inventoryPosition;
  const moqFloor = moq ?? 0;
  const flooredQty = Math.max(rawQty, moqFloor);
  const moqClamped = moqFloor > rawQty;

  const multiple = reorderMultiple ?? 0;
  let suggestedQty = flooredQty;
  let multipleRounded = false;
  if (multiple > 0) {
    const rounded = Math.ceil(flooredQty / multiple) * multiple;
    multipleRounded = rounded !== flooredQty;
    suggestedQty = rounded;
  }
  return { rawQty, suggestedQty, moqClamped, multipleRounded };
}

/**
 * Severity from the position (only reached when triggered, so position < rop):
 * high (position ≤ 0) · medium (position < rop × 0.5) · low (otherwise). Each
 * tier maps to a SemanticType via REORDER_SEVERITY_SEMANTICS.
 */
export function deriveSeverity(inventoryPosition: number, reorderPoint: number): ReorderSeverity {
  if (inventoryPosition <= 0) return 'high';
  if (inventoryPosition < reorderPoint * 0.5) return 'medium';
  return 'low';
}

/**
 * Coverage days = position ÷ avgDailyDemand, floored, capped at 365. `null`
 * when avgDailyDemand is absent or ≤ 0 (divide-by-zero guard) — the engine
 * never emits Infinity/NaN; the UI renders `—` / `>365d`. A ≤0 position yields 0.
 */
export function deriveCoverageDays(
  inventoryPosition: number,
  avgDailyDemand?: number | null
): number | null {
  if (avgDailyDemand == null || avgDailyDemand <= 0) return null;
  if (inventoryPosition <= 0) return 0;
  return Math.min(COVERAGE_DAYS_CAP, Math.floor(inventoryPosition / avgDailyDemand));
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * Compute the reorder suggestion for a single product, or `null` when the
 * product is EXCLUDED (blank params / doNotOrder / inactive / not-Ready) or
 * simply NOT TRIGGERED (inventoryPosition ≥ reorderPoint). The caller maps over
 * products and drops the nulls — the queue lists only triggered, eligible rows.
 */
export function computeReorderSuggestion(input: ReorderSuggestionInput): ReorderSuggestion | null {
  const { product, snapshot, params, supply, demand, avgDailyDemand, historyDays, computedAt } =
    input;

  if (suggestionExclusionReason(product, params) !== null) return null;
  // Exclusion guarantees both are non-null; the `?? 0` branch is never taken.
  const rop = params.reorderPoint ?? 0;
  const psl = params.preferredStockLevel ?? 0;

  const qtyOpenToReceive = supply.qtyOpenToReceive;
  const inventoryPosition = snapshot.availableQty + qtyOpenToReceive;

  // Trigger — strict: at ROP is NOT below (NetSuite parity, saved-search `<`).
  if (inventoryPosition >= rop) return null;

  const { rawQty, suggestedQty, moqClamped, multipleRounded } = computeSuggestedQty(
    psl,
    demand.pooledDemandQty,
    inventoryPosition,
    params.moq,
    params.reorderMultiple
  );

  const coverageDays = deriveCoverageDays(inventoryPosition, avgDailyDemand);

  // Confidence cause set. Freshness is owned by the SourceStampTag; the badge
  // rolls up only variance + thinHistory (kept separable via the cause set).
  const freshness = freshnessTierOf(snapshot.syncedAt, computedAt);
  const coverageActive = avgDailyDemand != null;
  const causes: ReorderConfidenceCauses = {};
  if (isStaleTier(freshness)) causes.stale = true;
  if (isReconciliationVariance(snapshot.qtyOnOrder, qtyOpenToReceive)) causes.variance = true;
  if (coverageActive && historyDays != null && historyDays < THIN_HISTORY_DAYS) {
    causes.thinHistory = true;
  }
  const confidence: ReorderConfidence =
    causes.stale || causes.variance || causes.thinHistory ? 'low' : 'ok';

  return {
    productId: product.productId,
    scn: product.scn ?? null,
    primaryVendorId: product.primaryVendorId,
    inventoryPosition,
    rop,
    psl,
    pooledDemandQty: demand.pooledDemandQty,
    qtyOpenToReceive,
    netsuiteQtyOnOrder: snapshot.qtyOnOrder,
    netsuiteDelta: reconciliationDelta(snapshot.qtyOnOrder, qtyOpenToReceive),
    suggestedQty,
    rawQty,
    moqClamped,
    multipleRounded,
    coverageDays,
    severity: deriveSeverity(inventoryPosition, rop),
    confidence,
    confidenceCauses: causes,
    freshness,
    syncedAt: snapshot.syncedAt,
    computedAt,
  };
}
